import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import { enqueueSnackbar } from "notistack";
import { useDebounceCallback, useResizeObserver } from "usehooks-ts";
import ReactPlayer from "react-player";
import { isInvalidUrl } from "../core/validation";
import { useGameSlugs, useMyProfile, useUpdateGameV2 } from "@seabrookstudios/pitch2table";
import { isPitch2TableRedirectUrl, trackedSizzleUrl } from "@seabrookstudios/pitch2table-core";
import { EditableListItemUrl } from "../core/EditableListItemUrl";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/types").Url} props.url
 */
export const UploadedSizzle = ({ gameId, url }) => {
  const { data: profile } = useMyProfile();
  const { data: slugs = [] } = useGameSlugs(gameId);
  const onUpdateGameSuccess = () => {
    enqueueSnackbar("Sizzle updated", { variant: "success" });
  };
  const onUpdateGameFailure = () => {
    enqueueSnackbar("Sizzle update failed", { variant: "error" });
  };

  const updateGame = useUpdateGameV2(gameId, onUpdateGameSuccess, onUpdateGameFailure);

  /**
   * @param {import("@seabrookstudios/types").Url} sizzle
   */
  const updateSizzleLink = (sizzle) => {
    if (isInvalidUrl(sizzle) || isPitch2TableRedirectUrl(sizzle)) {
      enqueueSnackbar("Please supply a valid video URL, not a Pitch2Table share link", { variant: "info" });
      return;
    }

    updateGame.mutate({
      sizzle,
      bgg: undefined,
      personalGoal: undefined,
      customAuthors: undefined,
      gameType: undefined,
      referenceId: undefined,
      tags: undefined,
      feedbackEnabled: undefined,
    });
  };

  const shareUrl = trackedSizzleUrl(url, profile?.slug, slugs[0]);

  const ref = useRef(null);
  const [{ width }, setSize] = useState(
    // @ts-expect-error ReactPlayer size state is untyped
    /** @type {Size} */ ({
      width: 0,
      height: 0,
    })
  );

  const onResize = useDebounceCallback(setSize, 200);

  useResizeObserver({
    ref,
    onResize,
  });

  return (
    <>
      <EditableListItemUrl
        noIcon
        onSave={updateSizzleLink}
        text={url}
        title="Sizzle Reel"
        helperText="A link to your sizzle reel hosted on another platform like YouTube. Copy shares a tracked Pitch2Table link when this game has a public short link."
        copyUrl={shareUrl}
      />
      {url && (
        <Box flex={1} justifyContent="center" alignItems="center" display="flex" ref={ref}>
          <ReactPlayer src={url} width={width - 32} height={((width - 32) / 320) * 180} />
        </Box>
      )}
    </>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").Url} props.url
 */
export const PublicSizzle = ({ url }) => {
  const ref = useRef(null);
  const [{ width }, setSize] = useState(
    // @ts-expect-error ReactPlayer size state is untyped
    /** @type {Size} */ ({
      width: 0,
      height: 0,
    })
  );

  const onResize = useDebounceCallback(setSize, 200);

  useResizeObserver({
    ref,
    onResize,
  });

  return (
    <Box flex={1} justifyContent="center" alignItems="center" display="flex" ref={ref}>
      <ReactPlayer src={url} width={width - 32} height={((width - 32) / 320) * 180} />
    </Box>
  );
};
